import { LoadingHeading } from '@zg-rentals/loading-components';
import { SetTourAvailabilityPrompt, UpcomingTours, useHasFutureTourAvailability } from '@zg-rentals/touring';
import { useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import { useScreenSize } from '../../../../hooks/useScreenSize';

import { getTourCalendarRoute } from '../../../../constants/urls';

import { useAuth } from '../../../../components/AuthProvider';

import {
  emitTourSchedulingUpsellSetTourAvailabilityCtaEvent,
  emitUpcomingToursMoreToursCtaEvent,
  TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID,
  UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID,
} from '../../../../analytics/events/overviewPage';
import ConnectedModuleCard from '../../../ConnectedModuleCard';

type Props = {
  listingAlias: string;
  isActive: boolean;
};

const UpcomingToursModule = ({ listingAlias, isActive }: Props) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const inquiriesPath = `/rental-manager/lead-management/${listingAlias}`;
  const { pathname: tourCalendarPathname } = getTourCalendarRoute({ listingAlias });
  const tourCalendarPath = `/rental-manager${tourCalendarPathname.replace('[listingAlias]', listingAlias)}`;

  const { data: touringTreatment, isLoading: isGetTouringTreatmentLoading } =
    useZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing(
      { listingAlias },
      { enabled: Boolean(listingAlias) },
    );

  const isTouringEnabled = Boolean(touringTreatment?.isTouringEnabledForListing);

  const { hasFutureAvailability, isLoading: isAvailabilityLoading } = useHasFutureTourAvailability(listingAlias, {
    enabled: isTouringEnabled,
  });

  const onCalendarClick = () => {
    if (user) {
      emitUpcomingToursMoreToursCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleTourSchedulingUpsellClick = () => {
    if (user) {
      emitTourSchedulingUpsellSetTourAvailabilityCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const isLoading = isGetTouringTreatmentLoading || (isTouringEnabled && isAvailabilityLoading);

  if (isLoading) {
    return (
      <ConnectedModuleCard
        isLoading
        css={{
          borderRadius: { base: 0, xxl: 'Card.Root' },
        }}
      >
        <LoadingHeading level={2} isLoading loadingWidth="100%">
          Upcoming Tours
        </LoadingHeading>
      </ConnectedModuleCard>
    );
  }

  if (!touringTreatment || !isTouringEnabled || !(isActive || touringTreatment.hasBookedTours)) {
    return null;
  }

  // Touring is enabled, but with no upcoming availability and no booked tours the "Upcoming tours"
  // widget would render empty and misleading — prompt the user to set availability instead.
  const hasUpcomingContent = hasFutureAvailability || touringTreatment.hasBookedTours;

  return (
    <ConnectedModuleCard
      css={{
        borderRadius: { base: 0, xxl: 'Card.Root' },
      }}
    >
      {hasUpcomingContent ? (
        <UpcomingTours
          inquiriesPath={inquiriesPath}
          isScreenMobile={isScreenMobile}
          listingAlias={listingAlias}
          onCalendarClick={onCalendarClick}
          tourCalendarPath={tourCalendarPath}
          eventId={UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID}
        />
      ) : (
        <SetTourAvailabilityPrompt
          tourCalendarPath={tourCalendarPath}
          onClick={handleTourSchedulingUpsellClick}
          eventId={TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default UpcomingToursModule;
